'use client'

import { Fragment, useEffect, useLayoutEffect, useRef, useState, type CSSProperties, type ReactNode } from 'react'
import type { OrderChatHistory, YMarketChatMessage } from '@/lib/ymarket-chat'
import type { ChatSendFlight } from '@/lib/chat-motion'

export interface PendingChatDelivery {
  text: string
  sentAt?: string
  status: 'sending' | 'checking' | 'confirmed' | 'delayed' | 'failed'
  attempt: number
  afterMessageId?: number
  dryRun?: boolean
  error?: string
}

const SENDER_LABEL: Record<YMarketChatMessage['sender'], string> = {
  PARTNER: 'Магазин', CUSTOMER: 'Покупатель', MARKET: 'Яндекс Маркет', SUPPORT: 'Поддержка',
}

// Reserved for the future manual-reply composer. Existing template buttons remain the only send surface.
const MANUAL_REPLY_ENABLED = false

function formatTime(value: string) {
  const date = new Date(value)
  return Number.isNaN(date.getTime()) ? value : date.toLocaleString('ru-RU', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })
}

function inlineMarkdown(text: string): ReactNode[] {
  const token = /(\*\*[^*]+\*\*|\*[^*]+\*|\[[^\]]+\]\(https?:\/\/[^)]+\)|https?:\/\/[^\s]+)/g
  return text.split(token).filter(Boolean).map((part, index) => {
    if (part.startsWith('**') && part.endsWith('**')) return <strong key={index} className="font-semibold">{part.slice(2, -2)}</strong>
    if (part.startsWith('*') && part.endsWith('*')) return <em key={index}>{part.slice(1, -1)}</em>
    const namedLink = part.match(/^\[([^\]]+)\]\((https?:\/\/[^)]+)\)$/)
    if (namedLink) return <a key={index} href={namedLink[2]} target="_blank" rel="noreferrer" className="font-medium text-gold-deep underline underline-offset-2">{namedLink[1]}</a>
    if (/^https?:\/\//.test(part)) return <a key={index} href={part} target="_blank" rel="noreferrer" className="font-medium text-gold-deep underline underline-offset-2">{part}</a>
    return <Fragment key={index}>{part}</Fragment>
  })
}

export function ChatMarkdown({ text }: { text: string }) {
  return <div className="break-words text-[13px] leading-relaxed">
    {text.split(/\r?\n/).map((line, index) => {
      const bullet = line.match(/^\s*[-•]\s+(.+)$/)
      if (bullet) return <div key={index} className="flex gap-2"><span aria-hidden>•</span><span>{inlineMarkdown(bullet[1])}</span></div>
      if (!line) return <div key={index} className="h-2" />
      return <div key={index}>{inlineMarkdown(line)}</div>
    })}
  </div>
}

function PendingStatus({ pending }: { pending: PendingChatDelivery }) {
  const checkingLabel = pending.attempt < 2 ? 'Ждём подтверждения от Яндекса…'
    : pending.attempt < 5 ? 'Проверяем чат Яндекса…'
      : pending.attempt < 10 ? 'Ещё немного, сообщение уже в пути…'
        : 'Почти готово — ждём появления в чате…'
  const label = pending.status === 'sending' ? 'Отправляем сообщение…'
    : pending.status === 'checking' ? pending.dryRun
      ? pending.attempt < 1 ? 'Тестируем отправку локально…' : 'Проверяем появление сообщения в чате…'
      : checkingLabel
      : pending.status === 'confirmed' ? pending.dryRun
        ? 'Тест завершён: сообщение показано локально, в Яндекс ничего не отправлено'
        : 'Отлично, сообщение появилось в чате Яндекса'
        : pending.status === 'delayed' ? 'Яндекс пока не показал сообщение. Можно обновить чат вручную.'
          : pending.error || 'Не удалось отправить сообщение'
  const success = pending.status === 'confirmed'
  const failed = pending.status === 'failed'
  const active = pending.status === 'sending' || pending.status === 'checking'
  return <div className={`ml-auto flex w-fit max-w-[88%] items-center gap-2 rounded-lg border px-3 py-2 text-[12px] ${success ? 'border-green-200 bg-green-50 text-green-800' : failed ? 'border-red-200 bg-red-50 text-red-700' : 'border-gold/30 bg-gold/10 text-ink-2'}`}>
    {active && <span aria-label="Отправка выполняется" className="chat-spinner shrink-0" />}
    {success && <span aria-hidden className="chat-success-mark">✓</span>}
    <span>{label}</span>
  </div>
}

export function YMarketChatPanel({ history, loading, error, open, pending, flight = null, onFlightComplete, onOpenChange, onRefresh }: {
  history: OrderChatHistory | null
  loading: boolean
  error: string
  open: boolean
  pending: PendingChatDelivery | null
  flight?: ChatSendFlight | null
  onFlightComplete?: () => void
  onOpenChange: (open: boolean) => void
  onRefresh: () => void
}) {
  const [dryRunMode, setDryRunMode] = useState(false)
  const messages = history?.messages.slice(-20) || []
  const showOptimistic = pending && pending.status !== 'confirmed'
  const messageViewportRef = useRef<HTMLDivElement>(null)
  const chatButtonRef = useRef<HTMLButtonElement>(null)
  const completedFlightRef = useRef<number | null>(null)
  const flightCompleteCallbackRef = useRef(onFlightComplete)
  const [flightVector, setFlightVector] = useState<{ x: number; y: number } | null>(null)
  const latestMessageId = messages.at(-1)?.messageId
  const keepNewestVisible = Boolean(pending)

  useEffect(() => {
    flightCompleteCallbackRef.current = onFlightComplete
  }, [onFlightComplete])

  useEffect(() => {
    if (!flight || !flightVector) return
    completedFlightRef.current = null
    const flightId = flight.id
    const timeout = window.setTimeout(() => {
      if (completedFlightRef.current === flightId) return
      completedFlightRef.current = flightId
      flightCompleteCallbackRef.current?.()
    }, 650)
    return () => window.clearTimeout(timeout)
  }, [flight, flightVector])

  useLayoutEffect(() => {
    if (!flight || !chatButtonRef.current) {
      setFlightVector(null)
      return
    }
    const target = chatButtonRef.current.getBoundingClientRect()
    setFlightVector({
      x: target.left + target.width / 2 - flight.x,
      y: target.top + target.height / 2 - flight.y,
    })
  }, [flight])

  useEffect(() => {
    setDryRunMode(import.meta.env.DEV
      || ['localhost', '127.0.0.1', '::1'].includes(window.location.hostname))
  }, [])

  useEffect(() => {
    if (!open || !messageViewportRef.current) return
    // Run after paint: Chromium's scroll anchoring can otherwise restore the old
    // offset after a layout effect and leave the first long bubble half hidden.
    const frame = requestAnimationFrame(() => {
      const viewport = messageViewportRef.current
      if (!viewport) return
      viewport.scrollTop = keepNewestVisible ? viewport.scrollHeight : 0
    })
    return () => cancelAnimationFrame(frame)
  }, [open, history?.chatId, latestMessageId, keepNewestVisible])

  return <>
    <button
      ref={chatButtonRef}
      type="button"
      aria-label={open ? 'Закрыть чат Яндекс Маркета' : 'Открыть чат Яндекс Маркета'}
      onClick={() => onOpenChange(!open)}
      className={`fixed z-50 right-4 sm:right-6 bottom-[calc(18px+env(safe-area-inset-bottom))] size-13 rounded-full border border-gold/40 bg-paper text-gold-deep shadow-[0_10px_35px_rgba(31,27,22,0.18)] grid place-items-center hover:bg-gold/10 ${flight ? 'chat-flight-target' : ''}`}
    >
      {open ? <span className="text-2xl leading-none">×</span> : <svg aria-hidden viewBox="0 0 24 24" className="size-6 fill-none stroke-current stroke-[1.8]"><path d="M5 5.5h14v10H9l-4 3v-13Z"/><path d="M8 9h8M8 12h5"/></svg>}
      {!open && pending && !['confirmed', 'failed'].includes(pending.status) && <span className="absolute right-0 top-0 size-3 rounded-full border-2 border-paper bg-gold-deep" />}
    </button>

    {flight && flightVector && <span
      key={flight.id}
      aria-hidden
      className="chat-send-flight"
      onAnimationEnd={() => {
        if (completedFlightRef.current === flight.id) return
        completedFlightRef.current = flight.id
        flightCompleteCallbackRef.current?.()
      }}
      style={{
        left: flight.x - 8,
        top: flight.y - 8,
        '--chat-flight-x': `${flightVector.x}px`,
        '--chat-flight-y': `${flightVector.y}px`,
        '--chat-flight-mid-x': `${flightVector.x * 0.62}px`,
        '--chat-flight-mid-y': `${flightVector.y * 0.62 - 30}px`,
      } as CSSProperties}
    />}

    {open && <section role="dialog" aria-label="Чат Яндекс Маркета" className="chat-panel-enter fixed z-40 right-4 sm:right-6 bottom-[calc(82px+env(safe-area-inset-bottom))] flex h-[min(680px,calc(100dvh-110px))] w-[calc(100vw-2rem)] max-w-[430px] origin-bottom-right flex-col overflow-hidden rounded-2xl border border-rule-strong bg-paper shadow-[0_20px_70px_rgba(31,27,22,0.24)]">
      <div className="flex items-center justify-between gap-3 border-b border-rule px-4 sm:px-5 py-4">
        <div className="min-w-0">
          <div className="flex items-center gap-2"><h3 className="truncate text-[17px] font-semibold">Чат Яндекс Маркета</h3>{dryRunMode && <span className="shrink-0 rounded-full bg-blue-50 px-2 py-0.5 text-[10px] font-semibold text-blue-700">DEV · без отправки</span>}{history?.confirmationCode && <span className="shrink-0 rounded-full bg-gold/15 px-2 py-0.5 text-[11px] font-semibold text-gold-deep">Код {history.confirmationCode}</span>}</div>
          <p className="mt-0.5 text-[11px] text-ink-muted">{dryRunMode ? 'Тестовый режим · пользователь ничего не получит' : 'Только этот заказ · обновление вручную'}</p>
        </div>
        <button className="btn btn-sm btn-ghost shrink-0" onClick={onRefresh} disabled={loading}>{loading ? 'Загрузка…' : 'Обновить'}</button>
      </div>
      {error && !pending ? <div className="m-4 border border-red-200 bg-red-50 px-3 py-2 text-[12px] text-red-700">{error}</div>
        : loading && !history && !pending ? <div className="p-5 text-[13px] text-ink-muted">Загружаем сообщения…</div>
        : !history?.chatId && !messages.length && !pending ? <div className="p-5 text-[13px] text-ink-muted">Для этого заказа чат пока не создан.</div>
        : !messages.length && !pending ? <div className="p-5 text-[13px] text-ink-muted">В чате пока нет сообщений.</div>
        : <div
          ref={messageViewportRef}
          data-testid="chat-viewport"
          style={{ overflowAnchor: 'none' }}
          className="min-h-0 flex-1 overscroll-contain overflow-y-auto p-4 sm:p-5 space-y-3"
        >
          {error && <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-[12px] text-red-700">История временно недоступна: {error}</div>}
          {loading && !history && <div className="text-[12px] text-ink-muted">История загружается, сообщение уже подготовлено ниже.</div>}
          {messages.map(message => {
            const justConfirmed = pending?.status === 'confirmed'
              && message.sender === 'PARTNER'
              && message.message?.trim() === pending.text.trim()
              && message.messageId > (pending.afterMessageId || 0)
            return <div key={message.messageId} className={`flex ${message.sender === 'PARTNER' ? 'justify-end' : 'justify-start'}`}>
            <div className={`max-w-[88%] overflow-visible rounded-xl border px-3.5 py-2.5 ${justConfirmed ? 'chat-message-confirmed' : ''} ${message.sender === 'PARTNER' ? 'border-gold/25 bg-gold/10 text-ink' : message.sender === 'MARKET' ? 'border-rule-strong bg-ivory text-ink' : 'border-transparent bg-bone text-ink'}`}>
              <div className="mb-1 text-[10px] font-semibold uppercase tracking-wide text-ink-muted">{SENDER_LABEL[message.sender]} · {formatTime(message.createdAt)}</div>
              {message.message && <ChatMarkdown text={message.message} />}
              {message.payload?.map((file, index) => <a key={index} href={file.url} target="_blank" rel="noreferrer" className="mt-1 block text-[12px] text-gold-deep underline">{file.name || 'Вложение'}</a>)}
            </div>
          </div>})}
          {showOptimistic && <div className="chat-pending-enter flex justify-end"><div className="w-full max-w-[92%] overflow-visible rounded-xl border border-gold/30 bg-gold/10 px-3.5 py-3 text-ink opacity-75"><div className="mb-2 flex items-center gap-2 text-[10px] font-semibold uppercase tracking-wide text-ink-muted">{!['failed', 'delayed'].includes(pending.status) && <span aria-hidden className="chat-spinner" />}<span>Магазин · {pending.status === 'failed' ? 'ошибка' : pending.status === 'delayed' ? 'ожидает подтверждения' : 'отправляется'}</span></div><ChatMarkdown text={pending.text} /></div></div>}
          {pending && <PendingStatus pending={pending} />}
        </div>}
      {MANUAL_REPLY_ENABLED && <div data-manual-reply-placeholder className="border-t border-rule p-3" />}
    </section>}
  </>
}
